import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { vehiclesAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Loader2, Truck, DollarSign, Wrench, Settings,
  ArrowLeftRight, ChevronDown, ChevronUp, Minus,
} from "lucide-react";

const StatCard = ({ label, valueA, valueB, format = "text" }) => {
  const fmt = (v) => {
    if (v === null || v === undefined) return "-";
    if (format === "currency") return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (format === "number") return Number(v).toLocaleString();
    return String(v);
  };

  const diff = format === "currency" || format === "number" ? (valueA || 0) - (valueB || 0) : null;

  return (
    <div className="grid grid-cols-3 gap-2 items-center py-2 border-b border-border last:border-0">
      <span className="text-xs font-mono font-bold text-right text-primary">{fmt(valueA)}</span>
      <div className="text-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        {diff !== null && diff !== 0 && (
          <div className="flex items-center justify-center mt-0.5">
            {diff > 0 ? (
              <Badge variant="outline" className="text-[10px] py-0 px-1 border-green-500/40 text-green-400">
                <ChevronUp className="w-3 h-3" />+{Math.abs(diff).toLocaleString()}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] py-0 px-1 border-red-500/40 text-red-400">
                <ChevronDown className="w-3 h-3" />{Math.abs(diff).toLocaleString()}
              </Badge>
            )}
          </div>
        )}
      </div>
      <span className="text-xs font-mono font-bold text-left text-primary">{fmt(valueB)}</span>
    </div>
  );
};

const CategoryBreakdown = ({ title, dataA, dataB }) => {
  const allCategories = [...new Set([
    ...Object.keys(dataA || {}),
    ...Object.keys(dataB || {}),
  ])].sort();

  if (allCategories.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="grid grid-cols-3 gap-2 pb-1 border-b border-border">
          <span className="text-[10px] text-muted-foreground text-right uppercase tracking-wide">Rig A</span>
          <span className="text-[10px] text-muted-foreground text-center uppercase tracking-wide">Category</span>
          <span className="text-[10px] text-muted-foreground text-left uppercase tracking-wide">Rig B</span>
        </div>
        {allCategories.map((cat) => {
          const a = (dataA || {})[cat];
          const b = (dataB || {})[cat];
          return (
            <div key={cat} className="grid grid-cols-3 gap-2 items-center py-1.5">
              <div className="text-right">
                {a ? (
                  <div>
                    <span className="text-xs font-bold">{a.count}</span>
                    <span className="text-[10px] text-muted-foreground ml-1">${(a.cost || 0).toLocaleString()}</span>
                  </div>
                ) : (
                  <Minus className="w-3 h-3 text-zinc-600 ml-auto" />
                )}
              </div>
              <span className="text-[10px] text-center text-muted-foreground truncate">{cat}</span>
              <div className="text-left">
                {b ? (
                  <div>
                    <span className="text-xs font-bold">{b.count}</span>
                    <span className="text-[10px] text-muted-foreground ml-1">${(b.cost || 0).toLocaleString()}</span>
                  </div>
                ) : (
                  <Minus className="w-3 h-3 text-zinc-600" />
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default function RigComparison() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rigAId, setRigAId] = useState("");
  const [rigBId, setRigBId] = useState("");
  const [rigAData, setRigAData] = useState(null);
  const [rigBData, setRigBData] = useState(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const [vRes, subRes] = await Promise.all([vehiclesAPI.list(), subscriptionAPI.status()]);
      setVehicles(vRes.data);
      setIsPro(subRes.data.is_pro);
    } catch {
      toast.error("Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!rigAId || !rigBId) { toast.error("Select two rigs to compare"); return; }
    if (rigAId === rigBId) { toast.error("Select two different rigs"); return; }
    setComparing(true);
    try {
      const [aRes, bRes] = await Promise.all([
        vehiclesAPI.comparisonData(rigAId),
        vehiclesAPI.comparisonData(rigBId),
      ]);
      setRigAData(aRes.data);
      setRigBData(bRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load comparison data");
    } finally {
      setComparing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <Crown className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold">Pro Feature</h2>
        <p className="text-muted-foreground text-center">Rig comparison is available with a Pro subscription.</p>
        <Button onClick={() => navigate("/subscription")}>Upgrade to Pro</Button>
      </div>
    );
  }

  const vA = rigAData?.vehicle;
  const vB = rigBData?.vehicle;
  const sA = rigAData?.stats;
  const sB = rigBData?.stats;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-primary" />
              Rig Comparison
            </h1>
            <p className="text-xs text-muted-foreground">Compare two builds side-by-side</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Rig Selection */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Rig A</Label>
                <Select value={rigAId} onValueChange={setRigAId}>
                  <SelectTrigger data-testid="rig-a-select">
                    <SelectValue placeholder="Select first rig" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id} disabled={v.id === rigBId}>
                        {v.nickname} ({v.year} {v.make})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-center">
                <div className="bg-primary/10 p-2 rounded-full">
                  <ArrowLeftRight className="w-5 h-5 text-primary" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Rig B</Label>
                <Select value={rigBId} onValueChange={setRigBId}>
                  <SelectTrigger data-testid="rig-b-select">
                    <SelectValue placeholder="Select second rig" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id} disabled={v.id === rigAId}>
                        {v.nickname} ({v.year} {v.make})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <Button
                onClick={handleCompare}
                disabled={comparing || !rigAId || !rigBId || rigAId === rigBId}
                data-testid="compare-btn"
                className="min-w-[160px]"
              >
                {comparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-2" />}
                Compare Rigs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        {rigAData && rigBData && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Vehicle Headers */}
            <div className="grid grid-cols-3 gap-2">
              <Card className="col-span-1">
                <CardContent className="pt-4 text-center">
                  <Truck className="w-8 h-8 mx-auto mb-1 text-primary" />
                  <p className="font-bold text-sm truncate">{vA?.nickname}</p>
                  <p className="text-xs text-muted-foreground">{vA?.year} {vA?.make} {vA?.model}</p>
                  <p className="text-xs text-muted-foreground mt-1">{(vA?.current_mileage || 0).toLocaleString()} mi</p>
                </CardContent>
              </Card>
              <div className="flex items-center justify-center">
                <span className="text-lg font-bold text-muted-foreground">VS</span>
              </div>
              <Card className="col-span-1">
                <CardContent className="pt-4 text-center">
                  <Truck className="w-8 h-8 mx-auto mb-1 text-primary" />
                  <p className="font-bold text-sm truncate">{vB?.nickname}</p>
                  <p className="text-xs text-muted-foreground">{vB?.year} {vB?.make} {vB?.model}</p>
                  <p className="text-xs text-muted-foreground mt-1">{(vB?.current_mileage || 0).toLocaleString()} mi</p>
                </CardContent>
              </Card>
            </div>

            {/* Stats Comparison */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Investment Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StatCard label="Total Mods" valueA={sA?.total_mods} valueB={sB?.total_mods} format="number" />
                <StatCard label="Mod Cost" valueA={sA?.total_mod_cost} valueB={sB?.total_mod_cost} format="currency" />
                <StatCard label="Services" valueA={sA?.total_services} valueB={sB?.total_services} format="number" />
                <StatCard label="Service Cost" valueA={sA?.total_service_cost} valueB={sB?.total_service_cost} format="currency" />
                <StatCard label="Total Invested" valueA={sA?.total_invested} valueB={sB?.total_invested} format="currency" />
              </CardContent>
            </Card>

            {/* Mods Breakdown */}
            <CategoryBreakdown
              title="Mods by Category"
              dataA={rigAData?.mods_by_category}
              dataB={rigBData?.mods_by_category}
            />

            {/* Services Breakdown */}
            <CategoryBreakdown
              title="Services by Category"
              dataA={rigAData?.services_by_category}
              dataB={rigBData?.services_by_category}
            />

            {/* Recent Mods */}
            {(rigAData.recent_mods?.length > 0 || rigBData.recent_mods?.length > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-blue-500" />
                    Recent Mods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      {(rigAData.recent_mods || []).map((m, i) => (
                        <div key={m.id || `mod-a-${i}`} className="text-xs p-2 rounded bg-muted/50">
                          <span className="font-medium">{m.item_name}</span>
                          {m.cost > 0 && <span className="text-muted-foreground ml-1">${m.cost}</span>}
                        </div>
                      ))}
                      {(!rigAData.recent_mods || rigAData.recent_mods.length === 0) && (
                        <p className="text-xs text-muted-foreground">No mods yet</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {(rigBData.recent_mods || []).map((m, i) => (
                        <div key={m.id || `mod-b-${i}`} className="text-xs p-2 rounded bg-muted/50">
                          <span className="font-medium">{m.item_name}</span>
                          {m.cost > 0 && <span className="text-muted-foreground ml-1">${m.cost}</span>}
                        </div>
                      ))}
                      {(!rigBData.recent_mods || rigBData.recent_mods.length === 0) && (
                        <p className="text-xs text-muted-foreground">No mods yet</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Services */}
            {(rigAData.recent_services?.length > 0 || rigBData.recent_services?.length > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4 text-orange-500" />
                    Recent Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      {(rigAData.recent_services || []).map((s, i) => (
                        <div key={s.id || `svc-a-${i}`} className="text-xs p-2 rounded bg-muted/50">
                          <span className="font-medium">{s.title || s.category}</span>
                          {s.cost > 0 && <span className="text-muted-foreground ml-1">${s.cost}</span>}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      {(rigBData.recent_services || []).map((s, i) => (
                        <div key={s.id || `svc-b-${i}`} className="text-xs p-2 rounded bg-muted/50">
                          <span className="font-medium">{s.title || s.category}</span>
                          {s.cost > 0 && <span className="text-muted-foreground ml-1">${s.cost}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty state */}
        {!rigAData && !rigBData && vehicles.length >= 2 && (
          <div className="text-center py-12 text-muted-foreground">
            <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Select two rigs above and click "Compare" to see a side-by-side breakdown.</p>
          </div>
        )}

        {vehicles.length < 2 && (
          <div className="text-center py-12 text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>You need at least 2 vehicles to compare.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/vehicles/new")}>
              Add a Vehicle
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

function Label({ children, className = "" }) {
  return <label className={`text-sm font-medium ${className}`}>{children}</label>;
}
